// 発注書ライフサイクル（注文済み・入荷・キャンセル）。
// Stocky の「Mark as ordered → Receive selected」に対応する自前DBの状態遷移。
// Shopify へ書くのは、入荷登録で「Shopifyの在庫に反映する」を明示的にオンに
// したときの在庫（available）加算だけ＝同期・日次パイプラインからは書かない。
// 転送（純正PO）には書き込まない。「注文済み」の未入荷数量はドラフトの
// 発注数量から自動で差し引かれる（二重発注防止）。
import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export type PlacedOrderStatus =
  "draft" | "placed" | "partially_received" | "received" | "cancelled";

export interface PlacedOrderItem {
  id: string;
  sku: string | null;
  title: string | null;
  /**
   * 現在の JAN（バーコードラベル印刷が読む）。
   * 🔴 sku/title と違い発注時のスナップショットではなく **常に現在の値**＝
   * 貼るラベルがいま Shopify にある JAN と一致しないと、貼った棚をスキャンしても引けない。
   */
  barcode: string | null;
  tax_rate_id: string | null;
  tax_rate_name: string | null;
  tax_rate: string | null;
  unit_price: string;
  ordered_qty: number;
  received_qty: number;
  remaining_qty: number;
}

export interface PlacedOrder {
  id: string;
  po_number: string;
  status: PlacedOrderStatus;
  supplier_id: string | null;
  supplier_name: string | null;
  ordered_at: string | null;
  expected_at: string | null;
  cancelled_at: string | null;
  note: string | null;
  items: PlacedOrderItem[];
}

export interface PlaceLineInput {
  shopify_variant_id: StockyPublicBigIntId;
  quantity: number;
  unit_price?: string;
  /** 明細ごとの税率（未指定＝商品の税率 → 店の既定へ縮退）。 */
  tax_rate_id?: StockyPublicBigIntId | null;
}

export interface PlaceOrderInput {
  supplier_id?: StockyPublicBigIntId | null;
  note?: string | null;
  expected_at?: string | null;
  /** Exact retained Stocky supplier PO-note revision adopted into `note`. */
  stocky_supplier_po_note_revision_id?: StockyPublicBigIntId | null;
  lines: PlaceLineInput[];
}

export interface ReceiveItemInput {
  purchase_order_item_id: StockyPublicBigIntId;
  quantity: number;
}

function canonicalNullableId(value: unknown, label: string): string | null {
  if (value == null) return null;
  try {
    return canonicalStockyPublicBigIntId(value);
  } catch {
    throw new TypeError(`${label} is invalid`);
  }
}

function normalizePlacedOrderItem(value: unknown): PlacedOrderItem {
  if (value === null || typeof value !== "object") {
    throw new TypeError("purchase order item response is invalid");
  }
  const item = value as Omit<PlacedOrderItem, "id" | "tax_rate_id"> & {
    id: unknown;
    tax_rate_id: unknown;
  };
  return {
    ...item,
    id: canonicalStockyPublicBigIntId(item.id),
    tax_rate_id: canonicalNullableId(item.tax_rate_id, "tax_rate_id"),
  };
}

export function normalizePlacedOrder(value: unknown): PlacedOrder {
  if (value === null || typeof value !== "object") {
    throw new TypeError("purchase order response is invalid");
  }
  const order = value as Omit<PlacedOrder, "id" | "supplier_id" | "items"> & {
    id: unknown;
    supplier_id: unknown;
    items: unknown;
  };
  if (!Array.isArray(order.items)) {
    throw new TypeError("purchase order items response is invalid");
  }
  return {
    ...order,
    id: canonicalStockyPublicBigIntId(order.id),
    supplier_id: canonicalNullableId(order.supplier_id, "supplier_id"),
    items: order.items.map(normalizePlacedOrderItem),
  };
}

export async function placePurchaseOrder(
  input: PlaceOrderInput,
  idempotencyKey: string,
): Promise<PlacedOrder> {
  if (
    input.stocky_supplier_po_note_revision_id != null &&
    !isStockyPublicBigIntId(input.stocky_supplier_po_note_revision_id)
  ) {
    throw new TypeError("invalid Stocky supplier note revision id");
  }
  const body: PlaceOrderInput = {
    ...input,
    lines: input.lines.map((line) => {
      const normalized: PlaceLineInput = {
        ...line,
        shopify_variant_id: canonicalStockyPublicBigIntId(
          line.shopify_variant_id,
        ),
      };
      if (line.tax_rate_id != null) {
        normalized.tax_rate_id = canonicalStockyPublicBigIntId(
          line.tax_rate_id,
        );
      }
      return normalized;
    }),
  };
  if (input.supplier_id != null) {
    body.supplier_id = canonicalStockyPublicBigIntId(input.supplier_id);
  }
  if (input.stocky_supplier_po_note_revision_id != null) {
    body.stocky_supplier_po_note_revision_id =
      canonicalStockyPublicBigIntId(
        input.stocky_supplier_po_note_revision_id,
      );
  }
  const result = await apiFetch<unknown>("/api/purchase-orders/placed", {
    method: "POST",
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return normalizePlacedOrder(result);
}

/**
 * 発注済みの発注書を新しい順に取得する。
 *
 * `query` は PO番号・仕入先名・**明細の SKU/商品名**の部分一致で絞る。
 * 🔴 絞り込みはサーバー側でやる＝一覧は最新 N 件までしか返さないので、
 * 手元にある分だけを絞ると**古い発注書が「無い」ことになる**（黙って見つからない）。
 */
export async function listPlacedOrders(
  query?: string,
): Promise<{ purchase_orders: PlacedOrder[] }> {
  const term = (query ?? "").trim();
  const path = term
    ? `/api/purchase-orders/placed?q=${encodeURIComponent(term)}`
    : "/api/purchase-orders/placed";
  const result = await apiFetch<{ purchase_orders?: unknown }>(path);
  if (!Array.isArray(result.purchase_orders)) {
    throw new TypeError("purchase order list response is invalid");
  }
  return { purchase_orders: result.purchase_orders.map(normalizePlacedOrder) };
}

export interface ReceiveOptions {
  /** true なら受領数を Shopify の在庫（available）へ差分反映する（要 locationId）。 */
  applyToShopify?: boolean;
  locationId?: StockyPublicBigIntId | null;
  /** 応答喪失後の再試行で使い回す UUID。入荷APIでは必須。 */
  idempotencyKey: string;
}

export async function receivePlacedItems(
  poId: StockyPublicBigIntId,
  items: ReceiveItemInput[],
  options: ReceiveOptions,
): Promise<PlacedOrder> {
  const canonicalPoId = canonicalStockyPublicBigIntId(poId);
  const result = await apiFetch<unknown>(`/api/purchase-orders/placed/${canonicalPoId}/receive`, {
    method: "POST",
    body: {
      items: items.map((item) => ({
        ...item,
        purchase_order_item_id: canonicalStockyPublicBigIntId(
          item.purchase_order_item_id,
        ),
      })),
      apply_to_shopify: options.applyToShopify ?? false,
      location_id:
        options.locationId == null
          ? null
          : canonicalStockyPublicBigIntId(options.locationId),
    },
    headers: { "Idempotency-Key": options.idempotencyKey },
  });
  return normalizePlacedOrder(result);
}

export async function cancelPlacedOrder(
  poId: StockyPublicBigIntId,
): Promise<PlacedOrder> {
  const result = await apiFetch<unknown>(`/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/cancel`, {
    method: "POST",
  });
  return normalizePlacedOrder(result);
}

// --- 確定後の編集（placed / partially_received のみ） ------------------------
// 🔴 発注数は入荷済み数より下げられない（backend が 422 で拒否・画面も手前で止める）。
// 行の削除は未入荷の明細だけ・最後の1行は消せない。派生値（発注提案の差引・
// 実原価の配賦）はすべてライブ計算＝編集がそのまま効く。

export interface PlacedHeaderPatchInput {
  /** undefined＝変更しない・null＝消去（JSON.stringify が undefined を落とす）。 */
  note?: string | null;
  expected_at?: string | null;
}

export async function updatePlacedOrder(
  poId: StockyPublicBigIntId,
  input: PlacedHeaderPatchInput,
): Promise<PlacedOrder> {
  const result = await apiFetch<unknown>(`/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}`, {
    method: "PATCH",
    body: input,
  });
  return normalizePlacedOrder(result);
}

export interface PlacedLinePatchInput {
  /** undefined＝変更しない（どちらも「消去」の意味は無い）。 */
  ordered_qty?: number;
  unit_price?: string;
}

export async function updatePlacedLine(
  poId: StockyPublicBigIntId,
  itemId: StockyPublicBigIntId,
  input: PlacedLinePatchInput,
): Promise<PlacedOrder> {
  const result = await apiFetch<unknown>(
    `/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/items/${canonicalStockyPublicBigIntId(itemId)}`,
    { method: "PATCH", body: input },
  );
  return normalizePlacedOrder(result);
}

export interface PlacedLineAddInput {
  shopify_variant_id: StockyPublicBigIntId;
  quantity: number;
  unit_price?: string;
  /** 未指定＝商品の税率 → 店の既定へ縮退（発注時と同じ解決）。 */
  tax_rate_id?: StockyPublicBigIntId | null;
}

export async function addPlacedLine(
  poId: StockyPublicBigIntId,
  input: PlacedLineAddInput,
  idempotencyKey: string,
): Promise<PlacedOrder> {
  const body: PlacedLineAddInput = {
    ...input,
    shopify_variant_id: canonicalStockyPublicBigIntId(
      input.shopify_variant_id,
    ),
  };
  if (input.tax_rate_id != null) {
    body.tax_rate_id = canonicalStockyPublicBigIntId(input.tax_rate_id);
  }
  const result = await apiFetch<unknown>(`/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/items`, {
    method: "POST",
    body,
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return normalizePlacedOrder(result);
}

export async function removePlacedLine(
  poId: StockyPublicBigIntId,
  itemId: StockyPublicBigIntId,
): Promise<PlacedOrder> {
  const result = await apiFetch<unknown>(
    `/api/purchase-orders/placed/${canonicalStockyPublicBigIntId(poId)}/items/${canonicalStockyPublicBigIntId(itemId)}`,
    { method: "DELETE" },
  );
  return normalizePlacedOrder(result);
}
